<script setup lang="ts">
import type { IResource } from '@bc-library/interface'
import { computed, defineEmits } from 'vue'
import { formatDistanceToNow } from 'date-fns'
import ResourceImage from '@/components/resource/resourceImage.vue'
import { useUserStore } from '@/config/store/userStore'
import { displayLocale } from '@/services/application/displayLocale'

const props = defineProps<{
  chat: {
    _id: string
    _created: Date
    authorId: string
    author: IResource
    message: string
    tagged: string[]
    taggedPopulated: IResource[]
  }
}>()

const userStore = useUserStore()
const dateFormatter = new Intl.DateTimeFormat(displayLocale.value, {
  year: 'numeric',
  month: 'long',
  weekday: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
})

const today = new Date()

const displayRelativeTime = computed(() => {
  return formatDistanceToNow(props.chat._created)
})

const isAuthor = computed(() => userStore.user._id === props.chat.author._id)
</script>

<template>
  <li class="relative flex flex-col gap-2">
    <div class="absolute top-0 right-0">
      <slot name="actions"></slot>
    </div>
    <div class="flex gap-4 items-end" :class="[{ 'flex-row-reverse': isAuthor }]">
      <ResourceImage :resource="chat.author" />
      <div
        class="p-2 card-base flex flex-col gap-2"
        :class="[{ 'items-end bg-linear-to-r from-primary-500 to-primary-400 dark:from-primary-700 dark:to-primary-800 text-gray-100': isAuthor }]"
      >
        <p v-html="chat.message" />
        <div class="flex justify-between gap-2 items-end">
          <p
            v-tooltip.top="dateFormatter.format(chat._created)" class="text-xs cursor-help"
            :class="[isAuthor ? 'text-slate-200' : 'subtitle']"
          >
            {{ displayRelativeTime }}
          </p>
          <div class="flex gap-2" :class="[{ 'flex-row-reverse': isAuthor }]">
            <ResourceImage v-for="tagged in chat.taggedPopulated" :key="tagged._id" :resource="tagged" size="w-5 h-5" />
          </div>
        </div>
      </div>
    </div>
  </li>
</template>
